import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Insights",
  cover_image: "",
  published: true,
};

const CATEGORY_OPTIONS = [
  { value: "News", label: "News — announcement, award, media, or lateral hire (appears on /news)" },
  { value: "Insights", label: "Insights — longform legal commentary (appears on /blog)" },
  { value: "Other", label: "Other — custom category (won't appear on /news or /blog)" },
];

export default function AdminPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) return;
    api.get(`/admin/posts/${id}`)
      .then(({ data }) => setForm({
        title: data.title || "",
        slug: data.slug || "",
        excerpt: data.excerpt || "",
        content: data.content || "",
        category: data.category || "Insights",
        cover_image: data.cover_image || "",
        published: !!data.published,
      }))
      .catch(() => { toast.error("Failed to load post"); navigate("/admin"); })
      .finally(() => setLoading(false));
  }, [id, isEditing, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      if (isEditing) {
        await api.put(`/admin/posts/${id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Post created");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-soft" data-testid="editor-loading">Loading…</div>;

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-post-editor">
      <header className="border-b border-line bg-bone-surface">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-ink hover:text-forest text-sm" data-testid="editor-back">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-gold-deep">{isEditing ? "Edit Post" : "New Post"}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl text-forest tracking-tight mb-8">{isEditing ? "Edit post" : "Write a new post"}</h1>

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={form.title} required onChange={(e) => update("title", e.target.value)} className="mt-2 bg-bone border-line" data-testid="editor-title" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="slug">Slug <span className="text-ink-soft text-xs">(optional — auto from title)</span></Label>
              <Input id="slug" value={form.slug} onChange={(e) => update("slug", e.target.value)} className="mt-2 bg-bone border-line font-mono text-sm" data-testid="editor-slug" />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={CATEGORY_OPTIONS.some((o) => o.value === form.category) ? form.category : "Other"}
                onChange={(e) => update("category", e.target.value === "Other" ? (form.category === "News" || form.category === "Insights" ? "" : form.category) : e.target.value)}
                className="mt-2 w-full bg-bone border border-line px-3 py-2 text-sm h-10 rounded-none"
                data-testid="editor-category"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.value}</option>
                ))}
              </select>
              {(!CATEGORY_OPTIONS.some((o) => o.value === form.category) || form.category === "") && (
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => update("category", e.target.value)}
                  placeholder="Custom category label"
                  className="mt-2 w-full bg-bone border border-line px-3 py-2 text-sm h-10 rounded-none"
                  data-testid="editor-category-custom"
                />
              )}
              <p className="text-[11px] text-ink-soft mt-1">
                {CATEGORY_OPTIONS.find((o) => o.value === form.category)?.label || "Custom category — won't route to /news or /blog"}
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="cover">Cover image URL</Label>
            <Input id="cover" type="url" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} className="mt-2 bg-bone border-line" placeholder="https://…" data-testid="editor-cover" />
          </div>

          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea id="excerpt" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} className="mt-2 bg-bone border-line min-h-[90px]" data-testid="editor-excerpt" />
          </div>

          <div>
            <Label htmlFor="content">Content</Label>
            <div id="content" className="mt-2">
              <RichTextEditor
                value={form.content}
                onChange={(html) => update("content", html)}
                placeholder="Write here — use the toolbar for headings, bold, italic, lists, links…"
                testId="editor-content"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 border border-line p-4 bg-bone-alt">
            <Switch id="published" checked={form.published} onCheckedChange={(v) => update("published", v)} data-testid="editor-published-switch" />
            <Label htmlFor="published" className="cursor-pointer">
              {form.published ? "Published — visible on the public site" : "Draft — hidden from the public site"}
            </Label>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" disabled={saving} className="bg-forest text-bone hover:bg-forest-light rounded-none px-7 py-5" data-testid="editor-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : isEditing ? "Save changes" : "Create post"}
            </Button>
            <Link to="/admin" className="text-ink-soft hover:text-forest text-sm" data-testid="editor-cancel">Cancel</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
